// src/pages/ManagerDashboard.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
  getUsers,
  getDepartments,
  createDepartment,
  assignUserToDepartment,
  removeUserFromDepartment,
  removeUserFromOrganization,
  createTask,
  getTasks,
  updateTask,
  deleteTask,
} from '../services/mockApi';
import { User, Department, Task } from '../types';
import { Container, FlexCenter } from '../styles/commonStyles';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';

const DashboardWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Sidebar = styled.nav<{ isOpen: boolean }>`
  width: 250px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: ${({ theme }) => theme.spacing.large};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  height: 100vh;
  z-index: 1000;
  transition: transform 0.3s ease-in-out;

  position: fixed;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 200px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: sticky;
    top: 0;
    transform: translateX(0);
    align-self: start;
  }
`;

const SidebarToggle = styled.button`
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1100;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const SidebarItem = styled.div<{ active: boolean }>`
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ active, theme }) =>
    active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MainContent = styled(Container)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.large};
  background-color: ${({ theme }) => theme.colors.background};
  margin-left: 250px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
    padding: ${({ theme }) => theme.spacing.medium};
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.6rem;
  }
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
  background: white;
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.3rem;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.muted};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const Input = styled.input`
  padding: 10px;
  margin-right: ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.muted};
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  max-width: 300px;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-right: 0;
    margin-bottom: ${({ theme }) => theme.spacing.small};
    max-width: 100%;
  }
`;

const Select = styled.select`
  padding: 10px;
  margin-right: ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.muted};
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  max-width: 300px;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-right: 0;
    margin-bottom: ${({ theme }) => theme.spacing.small};
    max-width: 100%;
  }
`;

const NotificationBanner = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1000;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    top: 60px;
    right: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
`;

const ManagerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [newTask, setNewTask] = useState({ title: '', description: '', assignedTo: '', departmentId: '' });
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [activeTab, setActiveTab] = useState('departments');
  const [notifications, setNotifications] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  // State to track department assignments for each employee in the "Assign Departments" tab
  const [departmentAssignments, setDepartmentAssignments] = useState<{ [userId: string]: string }>({});

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedUsers: User[] = await getUsers();
      const fetchedDepartments: Department[] = await getDepartments();
      const fetchedTasks: Task[] = await getTasks();
      setUsers(fetchedUsers);
      setDepartments(fetchedDepartments);
      setTasks(fetchedTasks);

      const unassignedEmployees = fetchedUsers.filter(
        (u) => u.role === 'employee' && !u.departmentId
      );
      if (unassignedEmployees.length > 0) {
        addNotification(`${unassignedEmployees.length} employee(s) need to be assigned to a department.`);
      }
    };
    fetchData();
  }, []);

  const addNotification = (message: string) => {
    setNotifications((prev) => [...prev, message]);
  };

  const removeNotification = (index: number) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateDepartment = async () => {
    if (!newDepartmentName || !user) return;
    const newDept = await createDepartment(newDepartmentName, user.id);
    setDepartments([...departments, newDept]);
    setNewDepartmentName('');
    addNotification(`Department "${newDept.name}" created successfully.`);
  };

  const handleAssignUserToDepartment = async (userId: string, departmentId: string) => {
    const updatedUser = await assignUserToDepartment(userId, departmentId);
    setUsers(users.map((u) => (u.id === userId ? updatedUser : u)));
    addNotification(
      `Employee "${updatedUser.username}" assigned to department "${departments.find((d) => d.id === departmentId)?.name}".`
    );
  };

  const handleMoveUser = async (userId: string, newDepartmentId: string) => {
    const updatedUser = await assignUserToDepartment(userId, newDepartmentId);
    setUsers(users.map((u) => (u.id === userId ? updatedUser : u)));
    addNotification(
      `Employee "${updatedUser.username}" moved to department "${departments.find((d) => d.id === newDepartmentId)?.name}".`
    );
  };

  const handleRemoveUserFromDepartment = async (userId: string) => {
    const updatedUser = await removeUserFromDepartment(userId);
    setUsers(users.map((u) => (u.id === userId ? updatedUser : u)));
    addNotification(`Employee "${updatedUser.username}" removed from department.`);
  };

  const handleRemoveUserFromOrganization = async (userId: string) => {
    const updatedUsers = await removeUserFromOrganization(userId);
    setUsers(updatedUsers);
    setTasks(tasks.filter((task) => task.assignedTo !== userId));
    addNotification(`Employee removed from organization.`);
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.description || !newTask.assignedTo || !newTask.departmentId || !user) return;
    const task = await createTask(
      newTask.title,
      newTask.description,
      newTask.assignedTo,
      newTask.departmentId,
      user.id
    );
    setTasks([...tasks, task]);
    setNewTask({ title: '', description: '', assignedTo: '', departmentId: '' });
    addNotification(`Task "${task.title}" created and assigned to "${users.find((u) => u.id === task.assignedTo)?.username}".`);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    const updatedTask = await updateTask(editingTask.id, {
      title: editingTask.title,
      description: editingTask.description,
      status: editingTask.status,
    });
    setTasks(tasks.map((t) => (t.id === editingTask.id ? updatedTask : t)));
    setEditingTask(null);
    addNotification(`Task "${updatedTask.title}" updated successfully.`);
  };

  const handleDeleteTask = async (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    await deleteTask(taskId);
    setTasks(tasks.filter((t) => t.id !== taskId));
    addNotification(`Task "${task?.title}" deleted.`);
  };

  const handleAssignDepartment = async (userId: string) => {
    const departmentId = departmentAssignments[userId];
    if (!departmentId) return;
    const updatedUser = await assignUserToDepartment(userId, departmentId);
    setUsers(users.map((u) => (u.id === userId ? updatedUser : u)));
    setDepartmentAssignments((prev) => ({ ...prev, [userId]: '' })); // Reset the dropdown
    addNotification(
      `Employee "${updatedUser.username}" assigned to department "${departments.find((d) => d.id === departmentId)?.name}".`
    );
  };

  const managerDepartments = departments.filter((dept) => dept.managerId === user?.id);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsSidebarOpen(false);
  };

  return (
    <DashboardWrapper>
      <SidebarToggle onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
        {isSidebarOpen ? '✕' : '☰'}
      </SidebarToggle>

      <Sidebar isOpen={isSidebarOpen}>
        <Title style={{ color: 'white', fontSize: '1.5rem' }}>Manager Portal</Title>
        <SidebarItem
          active={activeTab === 'departments'}
          onClick={() => {
            setActiveTab('departments');
            setIsSidebarOpen(false);
          }}
        >
          Departments
        </SidebarItem>
        <SidebarItem
          active={activeTab === 'employees'}
          onClick={() => {
            setActiveTab('employees');
            setIsSidebarOpen(false);
          }}
        >
          Employees
        </SidebarItem>
        <SidebarItem
          active={activeTab === 'assign-departments'}
          onClick={() => {
            setActiveTab('assign-departments');
            setIsSidebarOpen(false);
          }}
        >
          Assign Departments
        </SidebarItem>
        <SidebarItem
          active={activeTab === 'tasks'}
          onClick={() => {
            setActiveTab('tasks');
            setIsSidebarOpen(false);
          }}
        >
          Tasks
        </SidebarItem>
        <SidebarItem
          active={false}
          onClick={handleLogout}
        >
          Log Out
        </SidebarItem>
      </Sidebar>

      <MainContent>
        {/* <Title>Manager Dashboard</Title> */}
        {user && <SectionTitle>Welcome, {user.username}</SectionTitle>}

        {notifications.map((notification, index) => (
          <NotificationBanner key={index}>
            <span>{notification}</span>
            <CloseButton onClick={() => removeNotification(index)}>×</CloseButton>
          </NotificationBanner>
        ))}

        {activeTab === 'departments' && (
          <Section>
            <SectionTitle>Create Department</SectionTitle>
            <FlexCenter style={{ gap: '10px', flexDirection: 'column' }}>
              <Input
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
                placeholder="Department Name"
              />
              <Button as="button" onClick={handleCreateDepartment}>
                Create
              </Button>
            </FlexCenter>
          </Section>
        )}

        {activeTab === 'employees' && (
          <Section>
            <SectionTitle>Manage Employees</SectionTitle>
            {managerDepartments.map((dept) => (
              <Card key={dept.id}>
                <div>
                  <strong>{dept.name}</strong>
                  <div>
                    {users
                      .filter((u) => u.departmentId === dept.id)
                      .map((u) => (
                        <div key={u.id} style={{ margin: '8px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {u.username} ({u.role})
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Button
                              as="button"
                              onClick={() => handleRemoveUserFromDepartment(u.id)}
                              style={{ backgroundColor: '#ff4d4f' }}
                            >
                              Remove from Dept
                            </Button>
                            <Select
                              value=""
                              onChange={(e) => handleMoveUser(u.id, e.target.value)}
                            >
                              <option value="">Move to Dept</option>
                              {managerDepartments
                                .filter((d) => d.id !== dept.id)
                                .map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {d.name}
                                  </option>
                                ))}
                            </Select>
                            <Button
                              as="button"
                              onClick={() => handleRemoveUserFromOrganization(u.id)}
                              style={{ backgroundColor: '#ff4d4f' }}
                            >
                              Remove from Org
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                  <Select
                    value=""
                    onChange={(e) => handleAssignUserToDepartment(e.target.value, dept.id)}
                  >
                    <option value="">Add Employee</option>
                    {users
                      .filter((u) => !u.departmentId && u.role === 'employee')
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.username}
                        </option>
                      ))}
                  </Select>
                </div>
              </Card>
            ))}
          </Section>
        )}

        {activeTab === 'assign-departments' && (
          <Section>
            <SectionTitle>Assign Departments</SectionTitle>
            {users
              .filter((u) => u.role === 'employee' && u.id !== user?.id)
              .map((employee) => (
                <Card key={employee.id}>
                  <div style={{ flex: 1 }}>
                    <strong>{employee.username}</strong> ({employee.role})
                    <div>
                      Current Department: {departments.find((d) => d.id === employee.departmentId)?.name || 'Unassigned'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Select
                      value={departmentAssignments[employee.id] || ''}
                      onChange={(e) =>
                        setDepartmentAssignments((prev) => ({
                          ...prev,
                          [employee.id]: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Department</option>
                      {managerDepartments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </Select>
                    <Button
                      as="button"
                      onClick={() => handleAssignDepartment(employee.id)}
                      disabled={!departmentAssignments[employee.id]}
                    >
                      Assign
                    </Button>
                  </div>
                </Card>
              ))}
            {users.filter((u) => u.role === 'employee' && u.id !== user?.id).length === 0 && (
              <p>No employees available to assign.</p>
            )}
          </Section>
        )}

        {activeTab === 'tasks' && (
          <Section>
            <SectionTitle>Manage Tasks</SectionTitle>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Input
                  placeholder="Task Title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                />
                <Input
                  placeholder="Description"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                />
                <Select
                  value={newTask.departmentId}
                  onChange={(e) => setNewTask({ ...newTask, departmentId: e.target.value })}
                >
                  <option value="">Select Department</option>
                  {managerDepartments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Select>
                <Select
                  value={newTask.assignedTo}
                  onChange={(e) => setNewTask({ ...newTask, assignedTo: e.target.value })}
                >
                  <option value="">Assign To</option>
                  {users
                    .filter((u) => u.departmentId === newTask.departmentId && u.role === 'employee')
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                </Select>
                <Button as="button" onClick={handleCreateTask}>
                  Create Task
                </Button>
              </div>
            </Card>
            {tasks
              .filter((task) => managerDepartments.some((dept) => dept.id === task.departmentId))
              .map((task) => (
                <Card key={task.id}>
                  {editingTask?.id === task.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <Input
                        value={editingTask.title}
                        onChange={(e) =>
                          setEditingTask({ ...editingTask, title: e.target.value })
                        }
                      />
                      <Input
                        value={editingTask.description}
                        onChange={(e) =>
                          setEditingTask({ ...editingTask, description: e.target.value })
                        }
                      />
                      <Select
                        value={editingTask.status}
                        onChange={(e) =>
                          setEditingTask({ ...editingTask, status: e.target.value as Task['status'] })
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </Select>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button as="button" onClick={handleUpdateTask}>
                          Save
                        </Button>
                        <Button as="button" onClick={() => setEditingTask(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                      <div>
                        <strong>{task.title}</strong> - {task.description} ({task.status})
                        <div>
                          Assigned to: {users.find((u) => u.id === task.assignedTo)?.username}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button as="button" onClick={() => handleEditTask(task)}>
                          Edit
                        </Button>
                        <Button
                          as="button"
                          onClick={() => handleDeleteTask(task.id)}
                          style={{ backgroundColor: '#ff4d4f' }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
          </Section>
        )}
      </MainContent>
    </DashboardWrapper>
  );
};

export default ManagerDashboard;